import { CreateOrgReview, OrgReview } from "@garther/interfaces";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiClients } from "./context";

export const reviewsKeys = {
  all: ["reviews"] as const,
  lists: () => [...reviewsKeys.all, "list"] as const,
  list: (orgId: string) => [...reviewsKeys.lists(), orgId] as const
};

export function useReviews(options: { orgId: string; enabled?: boolean }) {
  const { orgApiClient } = useApiClients();
  const { orgId, enabled = true } = options;

  return useQuery({
    queryKey: reviewsKeys.list(orgId),
    queryFn: () => orgApiClient.getReviews(orgId),
    enabled: enabled && !!orgId,
    staleTime: 10 * 60 * 1000
  });
}

export function useReviewsInvalidation() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: reviewsKeys.all }),
    invalidateList: () => queryClient.invalidateQueries({ queryKey: reviewsKeys.lists() }),
    invalidateOrgReviews: (orgId: string) =>
      queryClient.invalidateQueries({ queryKey: reviewsKeys.list(orgId) })
  };
}

export function useCreateReviewMutation(
  options: { onSuccess?: (result: OrgReview) => void; onError?: (error: unknown) => void } = {}
) {
  const { orgApiClient } = useApiClients();
  const invalidate = useReviewsInvalidation();
  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: CreateOrgReview }) =>
      orgApiClient.createReview(orgId, data),
    onSuccess: (result, variables) => {
      invalidate.invalidateOrgReviews(variables.orgId);
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}
